import { UrlUtils } from 'msisdk-react';

import { ItemStore } from 'app/store/ItemStore';

/**
 * Сервис для работы с url'ом авторизации.
 *
 * Хранит актуальный url на сервис авторизации.
 * А также предоставляет функционал по получению коллбэк-url'а,
 * необходимого для перехода в приложение после авторизации.
 *
 * Url авторизации должен содержать параметр redirect_uri
 * (т.к. данный параметр содержит значение коллбэк-url'а).
 *
 * В проекте менеджер используется для получения значения пути коллбэк-url'а,
 * необходимого для перехода в приложение после авторизации в стороннем сервисе.
 * Использование:
 * - Необходимо задать значение url'а авторизации (setAuthorizeUrl()) до момента перехода на сервис авторизации.
 * - Необходимо указать во внутренней маршрутизации приложения значение пути коллбэк-url'а (getCallbackUrlPath()).
 *   До момента установки url'а авторизации значение коллбэк-url'а будет равно undefined.
 */
export class AuthorizeUrlService {
    /**
     * Наименование параметра коллбэк-url'а (указывается на стороне внешнего сервиса).
     */
    private readonly CALLBACK_URL_PARAM = 'redirect_uri';

    /**
     * Хранилище для значения url'а авторизации.
     * В качестве хранилища используется SessionStorage.
     */
    private readonly authorizeUrlStore: ItemStore<string> =
        new ItemStore<string>('authorizeUrl', sessionStorage);

    /**
     * Установить url авторизации.
     *
     * @param authorizeUrl url авторизации.
     * @throws {Error} при невалидном значении url'а авторизации или коллбэк-url'а.
     */
    setAuthorizeUrl(authorizeUrl: string): void {
        if (!UrlUtils.isValidUrl(authorizeUrl)) {
            throw new Error(`Invalid authorize url: "${authorizeUrl}"`);
        }

        const callbackUrl = this.getCallbackUrl(authorizeUrl);
        if (!UrlUtils.isValidUrl(callbackUrl)) {
            throw new Error(`Invalid callback url: "${callbackUrl}"`);
        }

        this.authorizeUrlStore.setItem(authorizeUrl);
    }

    /**
     * Получить url авторизации.
     *
     * @return url авторизации, если значение не задано = null.
     */
    getAuthorizeUrl(): string {
        return this.authorizeUrlStore.getItem();
    }

    /**
     * Получить путь коллбэк-url'а.
     *
     * @return путь коллбэк-url'а, если значение url не задано = undefined.
     */
    getCallbackUrlPath(): string {
        const authorizeUrl = this.getAuthorizeUrl();
        if (!authorizeUrl) {
            return undefined;
        }

        const callbackUrl = this.getCallbackUrl(authorizeUrl);
        return new URL(callbackUrl).pathname;
    }

    /**
     * Получить коллбэк-url.
     *
     * @param authorizeUrl url авторизации.
     * @return коллбэк-url, если значение url не найдено = undefined.
     */
    private getCallbackUrl(authorizeUrl: string): string {
        return UrlUtils.getUrlParam(this.CALLBACK_URL_PARAM, authorizeUrl);
    }
}

export const authorizeUrlService = new AuthorizeUrlService();
